import { useEffect, useState } from "react";
import { useM2, fmtM2 } from "@/lib/stats";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, imgUrl, SITE_URL, IS_BOT } from "@/lib/api";
import { getTileConfig } from "@/lib/mapTiles";
import JsonLd from "@/components/JsonLd";
import useTrackInView from "@/lib/useTrackInView";

const goldIcon = L.divIcon({
  className: "",
  html: `<div style="width:22px;height:22px;background:#D4AF37;border:2px solid #0A0A0A;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 0 0 3px rgba(212,175,55,0.25)"></div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 22],
  popupAnchor: [0, -22],
});

export default function ProjectsMap() {
  const [projects, setProjects] = useState([]);
  const m2 = fmtM2(useM2());
  const [okres, setOkres] = useState("");
  const [objType, setObjType] = useState("");
  const [tiles, setTiles] = useState(null);
  const viewRef = useTrackInView("view_map", "mapa");

  useEffect(() => { getTileConfig().then(setTiles); }, []);

  useEffect(() => {
    api.get("/references")
      .then((r) => setProjects(r.data.filter((p) => p.lat && p.lng)))
      .catch(() => {});
  }, []);

  const OBJ_LABELS = { dom: "Rodinné domy", bytovka: "Bytové domy", budova: "Budovy / objekty", podhlad: "Podhľady" };
  const okresy = [...new Set(projects.map((p) => p.okres).filter(Boolean))].sort((a, b) => a.localeCompare(b, "sk"));
  const objTypes = [...new Set(projects.map((p) => p.object_type).filter(Boolean))];
  const filtered = projects.filter((p) => (!okres || p.okres === okres) && (!objType || p.object_type === objType));

  // SEO/GEO vrstva (podľa projects-map-kit): ItemList of Places + geo meta pre AI crawlery.
  const mapLd = projects.length
    ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "Realizácie zateplenia — Zateplenia.eu",
        "url": `${SITE_URL}/#mapa`,
        "itemListElement": projects.map((p, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "item": {
            "@type": "Place",
            "name": p.title,
            "description": [p.type, p.area ? `${p.area} m²` : "", p.material, p.year].filter(Boolean).join(" · "),
            "image": p.image ? imgUrl(p.image) : undefined,
            "address": p.location,
            "geo": { "@type": "GeoCoordinates", "latitude": p.lat, "longitude": p.lng },
          },
        })),
      }
    : null;

  useEffect(() => {
    if (!projects.length) return;
    const lat = projects.reduce((s, p) => s + p.lat, 0) / projects.length;
    const lng = projects.reduce((s, p) => s + p.lng, 0) / projects.length;
    const setM = (n, c) => {
      let el = document.querySelector(`meta[name="${n}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", n); document.head.appendChild(el); }
      el.setAttribute("content", c);
    };
    setM("geo.region", "SK");
    setM("geo.placename", "Slovensko");
    setM("geo.position", `${lat.toFixed(4)};${lng.toFixed(4)}`);
    setM("ICBM", `${lat.toFixed(4)}, ${lng.toFixed(4)}`);
  }, [projects]);

  return (
    <section ref={viewRef} id="mapa" className="bg-ink py-24 md:py-32 border-t border-white/10">
      <JsonLd id="ld-map-projects" data={mapLd} />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Mapa realizácií</p>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.9] text-white">
              Naše projekty<br /><span className="text-gold">po celom Slovensku</span>
            </h2>
            <p className="mt-4 text-white/60 text-sm font-mono2 uppercase tracking-widest" data-testid="map-m2-counter">
              {m2}+ m² zateplených fasád — a stále viac
            </p>
          </div>
          <p className="text-white/50 max-w-sm text-base leading-relaxed">
            Kliknite na bod na mape a pozrite si fotku, popis a partnera konkrétnej realizácie.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <select value={okres} onChange={(e) => setOkres(e.target.value)} data-testid="map-filter-okres"
            className="bg-ink-2 border border-white/15 text-white/80 px-4 py-2 text-sm focus:border-gold outline-none">
            <option value="">Všetky okresy</option>
            {okresy.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <select value={objType} onChange={(e) => setObjType(e.target.value)} data-testid="map-filter-type"
            className="bg-ink-2 border border-white/15 text-white/80 px-4 py-2 text-sm focus:border-gold outline-none">
            <option value="">Všetky typy objektov</option>
            {objTypes.map((t) => <option key={t} value={t}>{OBJ_LABELS[t] || t}</option>)}
          </select>
          <span className="text-white/40 text-sm font-mono2" data-testid="map-filter-count">{filtered.length} realizácií</span>
          {(okres || objType) && (
            <button onClick={() => { setOkres(""); setObjType(""); }} data-testid="map-filter-reset" className="text-gold text-sm hover:underline">Zrušiť filter</button>
          )}
        </div>

        <div className="border border-white/10 overflow-hidden" data-testid="projects-map" style={{ height: "520px" }}>
          {tiles && !IS_BOT && (
          <MapContainer center={[48.72, 19.5]} zoom={7} scrollWheelZoom={false} style={{ height: "100%", width: "100%", background: "#0A0A0A" }}>
            <TileLayer
              attribution={tiles.attribution}
              url={tiles.url}
            />
            {filtered.map((p) => (
              <Marker key={p.id} position={[p.lat, p.lng]} icon={goldIcon}>
                <Popup>
                  <div style={{ width: "220px", fontFamily: "Manrope, Arial, sans-serif" }}>
                    <img src={imgUrl(p.image)} alt={p.title} style={{ width: "100%", height: "120px", objectFit: "cover" }} />
                    {(p.images || []).length > 1 && (
                      <div style={{ fontSize: "10px", color: "#A88A2A", fontWeight: 700, marginTop: "4px" }}>{p.images.length} fotiek</div>
                    )}
                    <div style={{ padding: "8px 2px" }}>
                      <div style={{ fontWeight: 700, color: "#0A0A0A" }}>{p.title} · {p.location}</div>
                      <div style={{ fontSize: "12px", color: "#555", margin: "4px 0" }}>{p.description}</div>
                      <div style={{ fontSize: "12px", color: "#111" }}>{p.material} · {p.area} m² · {p.year}</div>
                      {p.partner_name ? <div style={{ fontSize: "11px", color: "#A88A2A", marginTop: "4px" }}>Realizátor: {p.partner_name}</div> : null}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
          )}
        </div>
      </div>
    </section>
  );
}
